import React, { useState } from 'react';
import {Form, Input, Textarea, Button, Switch} from "@heroui/react";
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter
} from "@heroui/modal";

const OrgSettingsModal = ({user, dismissModal, setBanner, org}) => {
  const [orgDescr, setOrgDescr] = useState(org?.description || '');
  const [orgName, setOrgName] = useState(org?.name || '');
  const [copyrightText, setCopyrightText] = useState(org?.copyright_text || '');
  const [ipNotice, setIpNotice] = useState(org?.ip_notice || false);
  const [error, setError] = useState();
  const [subscribing, setSubscribing] = useState(false);

  // Check if user can edit copyright (admin or org owner/coadmin)
  const canEditCopyright = user?.admin || (org && user?.active_organization?.member?.role && ['admin', 'coadmin'].includes(user.active_organization.member.role));
  const isOrgOwner = org && (org.user_id === user?.id || org.user_id === user?._id);

  const subscribeWhiteLabel = async () => {
    setSubscribing(true);
    try {
      const fd = new FormData();
      fd.append('organization_id', org._id.$oid || org._id);
      fd.append('user_id', user.id || user._id);
      const res = await fetch('/api/v1/subscriptions/white_label', { method: 'POST', body: fd });
      const data = await res.json();
      if (data.checkout_url) {
        window.location.href = data.checkout_url;
      } else {
        setError(data.error || 'Failed to start subscription');
      }
    } catch (e) {
      setError('Failed to connect to payment service');
    }
    setSubscribing(false);
  };

  const cancelWhiteLabel = async () => {
    if (!window.confirm('Are you sure you want to cancel your white label subscription?')) return;
    try {
      const fd = new FormData();
      fd.append('organization_id', org._id.$oid || org._id);
      fd.append('user_id', user.id || user._id);
      const res = await fetch('/api/v1/subscriptions/white_label', { method: 'DELETE', body: fd });
      if (res.ok) {
        setBanner('White label subscription cancelled');
        dismissModal();
        window.location.reload();
      } else {
        const data = await res.json();
        setError(data.error || 'Failed to cancel');
      }
    } catch (e) {
      setError('Failed to connect to payment service');
    }
  };

  const saveSettings = async () => {
    let fd = new FormData();
    fd.append('description', orgDescr || '');
    fd.append('name', orgName);

    if (canEditCopyright) {
      fd.append('copyright_text', copyrightText || '');
      fd.append('ip_notice', ipNotice ? 'true' : 'false');
    }

    const d = await fetch(`/api/v1/organizations/${org._id}`, {method: 'PUT', body: fd});
    const orgJson = await d.json();

    if (d.status === 200) {
      setBanner(`Organization settings updated`);
      dismissModal();
      // Reload to get updated org data
      window.location.reload();
    } else {
      setError(`Error updating organization settings: ${d.status}`);
    }
  }

  return (
    <Form onSubmit={saveSettings}>
      <Modal placement='top' isOpen={true} isDismissable={true} isKeyboardDismissDisabled={true} onClose={dismissModal}>
        <ModalContent>
          <ModalHeader className='flex flex-col gap-1'>
            Organization Settings - {org?.name}
          </ModalHeader>
          <ModalBody>
            { error && <span style={{fontSize: '14px', color: '#ff0000'}}>{error}</span> }
            <div style={{display: 'flex', flexDirection: 'column'}}>
              <div style={{marginBottom: '10px'}}>
                <Input
                  label='Organization Name'
                  onChange={(d) => setOrgName(d.target.value)}
                  value={orgName}
                />
              </div>
              <div style={{marginBottom: '10px'}}>
                <Textarea
                  label='Description (optional)'
                  onChange={(d) => setOrgDescr(d.target.value)}
                  value={orgDescr}
                  placeholder='Describe your organization...'
                />
              </div>
              {canEditCopyright && (
                <div style={{marginBottom: '10px'}}>
                  <Input
                    label='Copyright Text (optional)'
                    placeholder='Example Co, All Rights Reserved'
                    onChange={(d) => setCopyrightText(d.target.value)}
                    value={copyrightText}
                    startContent={
                      <div style={{
                        fontSize: '14px',
                        color: '#666',
                        whiteSpace: 'nowrap',
                        paddingRight: '4px'
                      }}>
                        © {new Date().getFullYear()}
                      </div>
                    }
                    classNames={{
                      inputWrapper: 'border-orange-500 border-2'
                    }}
                  />
                  <div style={{fontSize: '12px', color: '#888', marginTop: '4px'}}>
                    This will be appended to all campaign emails from this organization
                  </div>
                </div>
              )}
              {canEditCopyright && (
                <div style={{marginBottom: '10px', display: 'flex', alignItems: 'center', gap: '10px'}}>
                  <Switch isSelected={ipNotice} onValueChange={setIpNotice} size="sm" />
                  <div>
                    <div style={{fontSize: '14px', fontWeight: 'bold'}}>Display IP Rights Notice</div>
                    <div style={{fontSize: '12px', color: '#888'}}>
                      Shows an intellectual property notice on the campaign landing page
                    </div>
                  </div>
                </div>
              )}
              {isOrgOwner && (
                <div style={{marginTop: '15px', padding: '15px', border: '2px solid #02A0B0', borderRadius: '8px', backgroundColor: '#f8f9fa'}}>
                  <div style={{fontWeight: 'bold', marginBottom: '8px', color: '#02A0B0'}}>🏷️ White Label Embed</div>
                  {org?.white_label ? (
                    <div>
                      <div style={{fontSize: '14px', color: '#2e7d32', marginBottom: '10px'}}>
                        ✅ Active — Your organization can use the embeddable widget on external sites.
                      </div>
                      <Button type="button" size="sm" color="danger" variant="flat" onPress={cancelWhiteLabel}>
                        Cancel Subscription
                      </Button>
                    </div>
                  ) : (
                    <div>
                      <div style={{fontSize: '14px', color: '#333', marginBottom: '10px'}}>
                        Embed your campaigns on any external website with a simple code snippet. Includes unlimited sending for all org campaigns.
                      </div>
                      <div style={{fontSize: '13px', color: '#666', marginBottom: '10px'}}>
                        <strong>$200/month</strong> — covers your entire organization
                      </div>
                      <Button type="button" size="sm" color="primary" isLoading={subscribing} onPress={subscribeWhiteLabel}>
                        Subscribe to White Label
                      </Button>
                    </div>
                  )}
                </div>
              )}
              <div style={{display: 'flex', flexDirection: 'row', justifyContent: 'flex-end', width: '100%'}}>
                <Button style={{marginTop: '10px'}} onPress={() => dismissModal()}>Cancel</Button>
                <Button style={{margin: '10px'}} onPress={() => saveSettings()} color='success'>Save</Button>
              </div>
            </div>
          </ModalBody>
          <ModalFooter>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Form>
  );
}

export default OrgSettingsModal;
